import React, { useEffect, useState, useRef, createElement } from 'react';
import { DownloadIcon, PrinterIcon, XIcon } from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { Button } from '../../components/ui/Button';
import { Encounter, Patient, User } from '../../types';
type PrescriptionFormPreviewProps = {
  encounter: Encounter;
  patient: Patient;
  pharmacist: User | null;
  onClose: () => void;
};
export function PrescriptionFormPreview({
  encounter,
  patient,
  pharmacist,
  onClose
}: PrescriptionFormPreviewProps) {
  const printableRef = useRef<HTMLElement>(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [pdfError, setPdfError] = useState('');
  const prescriptionDate = getPrescriptionDate(encounter);
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);
  const handlePrint = () => {
    document.body.classList.add('printing-test-modal');
    window.print();
    window.setTimeout(() => {
      document.body.classList.remove('printing-test-modal');
    }, 500);
  };
  const handleDownloadPdf = async () => {
    if (!printableRef.current) return;
    setIsGeneratingPdf(true);
    setPdfError('');
    try {
      const filename = `BMED_Prescription_${sanitizeFilename(patient.bmcFileNumber)}_${sanitizeFilename(patient.lastName)}.pdf`;
      await captureToA4Pdf(printableRef.current, filename);
    } catch (error) {
      console.error('Prescription PDF generation failed', error);
      setPdfError('The prescription PDF could not be generated. Please try again or use Print.');
    } finally {
      setIsGeneratingPdf(false);
    }
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4 print:bg-white print:p-0" role="dialog" aria-modal="true" aria-labelledby="prescription-form-title" data-id="element-5042">
      <div className="test-modal-print max-h-[95vh] w-full max-w-5xl overflow-y-auto rounded-xl bg-white shadow-2xl print:max-h-none print:max-w-none print:overflow-visible print:rounded-none print:shadow-none" data-id="element-5043">
        <header className="sticky top-0 z-10 flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 bg-white px-5 py-3 print:hidden" data-id="element-5044">
          <div data-id="element-5045">
            <h2 id="prescription-form-title" className="text-lg font-bold text-slate-900" data-id="element-5046">
              Prescription form
            </h2>
            <p className="text-sm text-slate-500" data-id="element-5047">
              Review, print, or download the patient prescription.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2" data-id="element-5048">
            <Button variant="outline" size="sm" onClick={handlePrint} data-id="element-5049">
              <PrinterIcon className="mr-1.5 h-4 w-4" data-id="element-5050" /> Print
            </Button>
            <Button size="sm" onClick={handleDownloadPdf} disabled={isGeneratingPdf} isLoading={isGeneratingPdf} className="bg-teal-600 hover:bg-teal-700 focus:ring-teal-500" data-id="element-5051">
              {!isGeneratingPdf && <DownloadIcon className="mr-1.5 h-4 w-4" data-id="element-5052" />}
              {isGeneratingPdf ? 'Generating PDF…' : 'Download PDF'}
            </Button>
            <button type="button" onClick={onClose} className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-teal-500" aria-label="Close prescription form" data-id="element-5053">
              <XIcon className="h-5 w-5" data-id="element-5054" />
            </button>
          </div>
        </header>

        {pdfError && <p role="alert" className="mx-5 mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 print:hidden" data-id="element-5055">
            {pdfError}
          </p>}

        <article ref={printableRef} className="a4-document bg-white p-7 text-slate-950 sm:p-10 print:p-0" data-id="element-5056">
          <header className="flex flex-col gap-5 border-b-2 border-slate-900 pb-5 sm:flex-row sm:items-start sm:justify-between" data-id="element-5057">
            <div className="flex items-center gap-4" data-id="element-5058">
              <img src="https://cdn.magicpatterns.com/uploads/3HmLapdTMRvTJbmSkdh1g8/Bmedhospital.png" alt="BMED Medical Centre" className="h-16 w-16 shrink-0 object-contain sm:h-20 sm:w-20" data-id="element-5059" />
              <div data-id="element-5060">
                <p className="text-xl font-bold tracking-wide text-pink-600 sm:text-2xl" data-id="element-5061">
                  BMED MEDICAL CENTRE
                </p>
                <p className="mt-1 text-sm italic text-slate-600" data-id="element-5062">
                  Caring Healing Leading
                </p>
              </div>
            </div>
            <div className="text-left sm:text-right" data-id="element-5063">
              <h1 className="text-xl font-bold tracking-[0.12em] text-slate-900 sm:text-2xl" data-id="element-5064">
                PRESCRIPTION FORM
              </h1>
              <p className="mt-3 text-sm" data-id="element-5065">
                <span className="font-semibold" data-id="element-5066">Prescription date:</span>{' '}
                {formatDate(prescriptionDate)}
              </p>
              <p className="text-sm" data-id="element-5067">
                <span className="font-semibold" data-id="element-5068">Encounter date:</span>{' '}
                {formatDate(encounter.startedAt)}
              </p>
              <p className="text-sm" data-id="element-5069">
                <span className="font-semibold" data-id="element-5070">Encounter:</span> {encounter.id}
              </p>
            </div>
          </header>

          <section className="my-7 border-2 border-slate-800 text-sm" aria-label="Patient details" data-id="element-5071">
            <div className="grid gap-x-5 gap-y-3 border-b border-slate-300 p-4 sm:grid-cols-[1.8fr_0.6fr_0.6fr]" data-id="element-5072">
              <p data-id="element-5073">
                <span className="font-semibold" data-id="element-5074">Patient name:</span>{' '}
                {patient.firstName} {patient.lastName}
              </p>
              <p data-id="element-5075">
                <span className="font-semibold" data-id="element-5076">Age:</span> {patient.age}
              </p>
              <p data-id="element-5077">
                <span className="font-semibold" data-id="element-5078">Sex:</span> {patient.sex}
              </p>
            </div>
            <div className="grid gap-x-5 gap-y-3 border-b border-slate-300 p-4 sm:grid-cols-2" data-id="element-5079">
              <p data-id="element-5080">
                <span className="font-semibold" data-id="element-5081">File no:</span>{' '}
                {patient.bmcFileNumber}
              </p>
              <p data-id="element-5082">
                <span className="font-semibold" data-id="element-5083">Prescribing clinician:</span>{' '}
                {encounter.lastAttendingClinician || 'Not recorded'}
              </p>
            </div>
            <div className="p-4" data-id="element-5084">
              <span className="font-semibold" data-id="element-5085">Known allergies:</span>{' '}
              <span className="font-semibold text-red-700" data-id="element-5086">
                {patient.allergies || 'None recorded'}
              </span>
            </div>
          </section>

          <section className="no-break" aria-labelledby="prescription-medications-title" data-id="element-5087">
            <h2 id="prescription-medications-title" className="border-2 border-b-0 border-slate-800 bg-slate-100 px-4 py-2 text-center text-sm font-bold uppercase tracking-wide" data-id="element-5088">
              Prescribed medication
            </h2>
            <div className="overflow-x-auto" data-id="element-5089">
              <table className="w-full min-w-[660px] border-collapse text-left text-sm" data-id="element-5090">
                <thead data-id="element-5091">
                  <tr className="bg-slate-50" data-id="element-5092">
                    <th scope="col" className="w-12 border border-slate-800 p-2 text-center" data-id="element-5093">
                      No.
                    </th>
                    <th scope="col" className="border border-slate-800 p-2" data-id="element-5094">
                      Drug
                    </th>
                    <th scope="col" className="border border-slate-800 p-2" data-id="element-5095">
                      Dose
                    </th>
                    <th scope="col" className="border border-slate-800 p-2" data-id="element-5096">
                      Route
                    </th>
                    <th scope="col" className="border border-slate-800 p-2" data-id="element-5097">
                      Frequency
                    </th>
                    <th scope="col" className="border border-slate-800 p-2" data-id="element-5098">
                      Days
                    </th>
                  </tr>
                </thead>
                <tbody data-id="element-5099">
                  {encounter.prescriptions.map((prescription, index) => <tr key={prescription.id} data-id="element-5100">
                      <td className="border border-slate-800 p-2 text-center" data-id="element-5101">
                        {index + 1}
                      </td>
                      <td className="border border-slate-800 p-2 font-semibold" data-id="element-5102">
                        {prescription.drugName}
                      </td>
                      <td className="border border-slate-800 p-2" data-id="element-5103">
                        {prescription.dose}
                      </td>
                      <td className="border border-slate-800 p-2" data-id="element-5104">
                        {prescription.route}
                      </td>
                      <td className="border border-slate-800 p-2" data-id="element-5105">
                        {prescription.frequency}
                      </td>
                      <td className="border border-slate-800 p-2" data-id="element-5106">
                        {prescription.days}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </section>

          <footer className="signature-block mt-16 grid gap-10 border-t border-slate-300 pt-7 sm:grid-cols-2" data-id="element-5107">
            <div data-id="element-5108">
              <div className="border-b border-slate-900 pb-1 text-sm font-medium" data-id="element-5109">
                {encounter.lastAttendingClinician || ' '}
              </div>
              <p className="mt-2 text-xs font-semibold uppercase tracking-wide text-slate-600" data-id="element-5110">
                Prescribing clinician signature
              </p>
            </div>
            <div data-id="element-5111">
              <div className="border-b border-slate-900 pb-1 text-sm font-medium" data-id="element-5112">
                {pharmacist?.fullName || ' '}
              </div>
              <p className="mt-2 text-xs font-semibold uppercase tracking-wide text-slate-600" data-id="element-5113">
                Generated by pharmacist
              </p>
            </div>
          </footer>
        </article>
      </div>
    </div>;
}
function getPrescriptionDate(encounter: Encounter) {
  const dates = encounter.prescriptions.map(prescription => prescription.prescribedAt).filter(Boolean).map(value => new Date(value)).filter(value => !Number.isNaN(value.getTime()));
  return dates.length ? dates[0].toISOString() : encounter.startedAt;
}
function formatDate(value: string) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString();
}
function sanitizeFilename(value: string) {
  return value.replace(/[^a-z0-9-_]/gi, '_') || 'patient';
}
async function captureToA4Pdf(element: HTMLElement, filename: string) {
  const pageWidth = 210;
  const pageHeight = 297;
  const marginTop = 17.78;
  const marginRight = 10.16;
  const marginBottom = 17.78;
  const marginLeft = 12.7;
  const contentWidth = pageWidth - marginLeft - marginRight;
  const contentHeight = pageHeight - marginTop - marginBottom;
  const sourceCanvas = await html2canvas(element, {
    scale: 2,
    useCORS: true,
    backgroundColor: '#ffffff',
    logging: false
  });
  const pdf = new jsPDF({
    unit: 'mm',
    format: 'a4',
    orientation: 'portrait',
    compress: true
  });
  const pixelsPerMillimetre = sourceCanvas.width / contentWidth;
  const sliceHeight = Math.floor(contentHeight * pixelsPerMillimetre);
  let cursor = 0;
  let page = 0;
  while (cursor < sourceCanvas.height) {
    const height = Math.min(sliceHeight, sourceCanvas.height - cursor);
    const slice = document.createElement('canvas');
    slice.width = sourceCanvas.width;
    slice.height = height;
    const context = slice.getContext('2d');
    if (!context) break;
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, slice.width, slice.height);
    context.drawImage(sourceCanvas, 0, cursor, sourceCanvas.width, height, 0, 0, slice.width, height);
    if (page > 0) pdf.addPage();
    pdf.addImage(slice.toDataURL('image/jpeg', 0.95), 'JPEG', marginLeft, marginTop, contentWidth, height / pixelsPerMillimetre);
    cursor += height;
    page += 1;
  }
  pdf.save(filename);
}